/**
 * BatchNotificationContext
 *
 * Cross-component notification system for batch creation events.
 * Provides visual indicators on the Picker nav and ARIA live region
 * announcements for screen readers.
 *
 * Usage:
 * 1. Wrap your app with <BatchNotificationProvider>
 * 2. Call notifyBatchCreated(batchNumber) when batch is created
 * 3. Use showPickerIndicator to display green indicator on Picker nav
 * 4. ARIA live region automatically announces batch creation
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// Duration constants (extracted from magic numbers)
const INDICATOR_DURATION = 3000; // 3 seconds

interface BatchNotificationContextValue {
  /** Whether to show the green indicator on Picker nav */
  showPickerIndicator: boolean;
  /** Call when a batch is created to trigger notification */
  notifyBatchCreated: (batchNumber?: string) => void;
  /** Last created batch number for ARIA announcement */
  lastBatchNumber: string | null;
}

const BatchNotificationContext = createContext<BatchNotificationContextValue | null>(null);

export function BatchNotificationProvider({ children }: { children: React.ReactNode }) {
  const [showPickerIndicator, setShowPickerIndicator] = useState(false);
  const [lastBatchNumber, setLastBatchNumber] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const notifyBatchCreated = useCallback((batchNumber?: string) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setShowPickerIndicator(true);
    setLastBatchNumber(batchNumber ?? 'New batch');

    // Auto-clear after INDICATOR_DURATION
    timeoutRef.current = setTimeout(() => {
      setShowPickerIndicator(false);
      setLastBatchNumber(null);
      timeoutRef.current = null;
    }, INDICATOR_DURATION);
  }, []);

  return (
    <BatchNotificationContext.Provider value={{ showPickerIndicator, notifyBatchCreated, lastBatchNumber }}>
      {children}
      {/* ARIA live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {showPickerIndicator && lastBatchNumber && (
          `Batch ${lastBatchNumber} created successfully. View in Picker.`
        )}
      </div>
    </BatchNotificationContext.Provider>
  );
}

/**
 * Hook to access batch notification context.
 * Returns safe defaults when used outside provider (graceful degradation).
 * This allows ProductionNav to render in Header without crashing during
 * workspace transitions before navigation completes.
 */
export function useBatchNotification(): BatchNotificationContextValue {
  const context = useContext(BatchNotificationContext);
  if (!context) {
    // Return safe defaults - allows component to render without provider
    // This happens during workspace transitions when Header renders ProductionNav
    // before the production layout with BatchNotificationProvider is mounted
    return {
      showPickerIndicator: false,
      notifyBatchCreated: () => {}, // No-op when outside provider
      lastBatchNumber: null,
    };
  }
  return context;
}

// Re-export for convenience
export { BatchNotificationContext };
